/**
 * @module schedules
 * @description Cron-based task scheduling with persistence and audit
 */

import type { ModuleManifest } from '@gzl10/nexus-sdk'
import { scheduleEntity, scheduleLogEntity, nextRunsAction, functionsAction } from './schedules.entity.js'
import { SchedulesService } from './schedules.service.js'
import { createSchedulesRoutes } from './schedules.routes.js'

export { scheduleEntity, scheduleLogEntity }
export { SchedulesService }
export { registerFunction, unregisterFunction, listRegisteredFunctions, clearFunctionRegistry, hasFunctionRegistered } from './schedules.executor.js'
export type {
  Schedule,
  ScheduleStatus,
  RetryBackoff,
  CreateScheduleInput,
  UpdateScheduleInput,
  ScheduleFunction,
  SchedulesApi,
  ExecutionResult,
  NextRunInfo
} from './schedules.types.js'

// Track shutdown unsubscribe for proper cleanup on re-init
let unsubscribeShutdown: (() => void) | null = null

export const schedulesModule: ModuleManifest = {
  name: 'schedules',
  label: { en: 'Schedules', es: 'Programaciones' },
  icon: 'mdi:clock-outline',
  description: {
    en: 'Cron-based task scheduling with persistence and audit',
    es: 'Programación de tareas con expresiones cron, persistencia y auditoría'
  },
  type: 'core',
  category: 'jobs',
  definitions: [scheduleEntity, scheduleLogEntity],
  actions: [nextRunsAction, functionsAction],

  pages: [
    {
      id: 'schedules-monitor',
      label: { en: 'Job Monitor', es: 'Monitor de tareas' },
      icon: 'mdi:monitor-dashboard',
      type: 'dashboard',
      order: 0,
      widgets: [
        { id: 'stats', type: 'stat', endpoint: '/schedules/stats', layout: { span: 12 } },
        { id: 'next-runs', type: 'table', label: { en: 'Upcoming runs', es: 'Próximas ejecuciones' }, endpoint: '/schedules/next-runs', layout: { span: 6 } },
        { id: 'recent-logs', type: 'table', label: { en: 'Recent executions', es: 'Ejecuciones recientes' }, endpoint: '/schedules/recent-logs', layout: { span: 6 } }
      ]
    }
  ],

  routes: createSchedulesRoutes,

  routePrefix: '/schedules',

  init: async (ctx) => {
    const service = new SchedulesService(ctx)
    ctx.services.register('scheduler', service)

    const disabled = process.env['NEXUS_SCHEDULES_DISABLED'] === 'true'
    if (disabled) {
      ctx.core.logger.warn('Schedules execution disabled via NEXUS_SCHEDULES_DISABLED — cron jobs will not run')
      return
    }

    // Clean up previous shutdown listener on re-init
    if (unsubscribeShutdown) unsubscribeShutdown()
    const handler = () => service.shutdown()
    ctx.events.on('server.stopping', handler)
    unsubscribeShutdown = () => ctx.events.off('server.stopping', handler)

    // Init when server is fully ready (routes mounted, DB ready)
    ctx.events.on('server.started', async () => {
      try {
        await service.init()

        // Register cleanup for own tables only
        service.registerFunction('schedules:cleanup-logs', async () => {
          const cutoff = ctx.db.formatTimestamp(ctx.db.knex, new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
          const deleted = await ctx.db.knex('schedule_logs').where('started_at', '<', cutoff).delete()
          return { deleted, table: 'schedule_logs', cutoff }
        })

        await service.ensureSystemSchedule({
          name: '__system:cleanup-logs',
          description: 'Purge schedule_logs older than 30 days',
          cron_expression: '0 3 * * *',
          function_key: 'schedules:cleanup-logs'
        })

        // Notify other modules so they register their functions.
        // registerFunction() is synchronous and runs before each listener's first await,
        // so all functions are in the registry by the time emit() returns.
        ctx.events.notify('schedules.ready', service)

        // Now check for orphans — all modules have registered their functions
        await service.checkOrphanFunctions()

      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        ctx.core.logger.error({ err: error }, `Failed to initialize schedules: ${message}`)
      }
    })

    ctx.core.logger.debug('Schedules module initialized')
  }
}
